import React from 'react';

/**
 * Reusable ReactMarkdown component renderers for chat messages
 * 
 * These are defined OUTSIDE of any component to avoid the "unstable nested components" issue.
 * React would otherwise see new component types on every render and destroy DOM state.
 * 
 * Usage:
 *   import { chatMarkdownComponents, recipeMarkdownComponents } from './MarkdownRenderers';
 *   <ReactMarkdown components={chatMarkdownComponents}>{content}</ReactMarkdown>
 */

// ============================================
// Chat Message Renderers (for DraggableChat)
// ============================================

const ChatStrong = ({ node, children, ...props }) => {
  const text = String(children);
  const isSectionHeader = text.includes('Ingredients') || 
                          text.includes('Instructions') || 
                          text.includes('Tips') || 
                          text.includes('Notes') || 
                          text.includes('Tricks');
  return (
    <span className={`font-bold ${isSectionHeader ? 'block mt-2 mb-1' : ''}`} {...props}>
      {children}
    </span>
  );
};

const ChatEm = ({ node, ...props }) => (
  <span className="italic text-gray-600" {...props} />
);

const ChatP = ({ node, ...props }) => (
  <p className="my-1" {...props} />
);

const ChatUl = ({ node, ...props }) => (
  <ul className="list-disc pl-4 my-1" {...props} />
);

const ChatOl = ({ node, ...props }) => (
  <ol className="list-decimal pl-4 my-1" {...props} />
);

const ChatLi = ({ node, ...props }) => (
  <li className="my-0.5" {...props} />
);

const ChatH1 = ({ node, ...props }) => (
  <h1 className="text-lg font-bold mt-3 mb-1" {...props} />
);

const ChatH2 = ({ node, ...props }) => (
  <h2 className="text-base font-bold mt-2 mb-1" {...props} />
);

const ChatH3 = ({ node, ...props }) => (
  <h3 className="text-sm font-bold mt-2 mb-0.5" {...props} />
);

/**
 * Components for DraggableChat and similar chat UIs
 */
export const chatMarkdownComponents = {
  strong: ChatStrong,
  em: ChatEm,
  p: ChatP,
  ul: ChatUl,
  ol: ChatOl,
  li: ChatLi,
  h1: ChatH1,
  h2: ChatH2,
  h3: ChatH3,
};


// ============================================
// Recipe Content Renderers (for ChatRecipeCreator)
// ============================================

const RecipeP = ({ node, ...props }) => (
  <p className="mb-3 last:mb-0" {...props} />
);

const RecipeUl = ({ node, ...props }) => (
  <ul className="list-disc pl-5 mb-3 space-y-1" {...props} />
);

const RecipeOl = ({ node, ...props }) => (
  <ol className="list-decimal pl-5 mb-3 space-y-1" {...props} />
);

const RecipeLi = ({ node, ...props }) => (
  <li className="mb-1" {...props} />
);

const RecipeStrong = ({ node, ...props }) => (
  <strong className="font-semibold text-gray-900" {...props} />
);

const RecipeH1 = ({ node, ...props }) => (
  <h1 className="text-xl font-bold mb-2 text-gray-900" {...props} />
);

const RecipeH2 = ({ node, ...props }) => (
  <h2 className="text-lg font-bold mb-2 text-gray-900" {...props} />
);

const RecipeH3 = ({ node, ...props }) => (
  <h3 className="text-base font-semibold mb-2 text-gray-800" {...props} />
);

const RecipeEm = ({ node, ...props }) => (
  <span className="italic text-gray-600" {...props} />
);

/**
 * Components for recipe content in ChatRecipeCreator
 */
export const recipeMarkdownComponents = {
  p: RecipeP,
  ul: RecipeUl,
  ol: RecipeOl,
  li: RecipeLi,
  strong: RecipeStrong,
  h1: RecipeH1,
  h2: RecipeH2,
  h3: RecipeH3,
  em: RecipeEm,
};


// ============================================
// Compact Chat Renderers (for smaller chat bubbles)
// ============================================

const CompactP = ({ node, ...props }) => (
  <p className="mb-1 last:mb-0" {...props} />
);

const CompactUl = ({ node, ...props }) => (
  <ul className="list-disc pl-4 mb-1 space-y-0.5" {...props} />
);

const CompactOl = ({ node, ...props }) => (
  <ol className="list-decimal pl-4 mb-1 space-y-0.5" {...props} />
);

const CompactLi = ({ node, ...props }) => (
  <li className="mb-0.5" {...props} />
);

const CompactStrong = ({ node, ...props }) => (
  <strong className="font-semibold" {...props} />
);

const CompactEm = ({ node, ...props }) => (
  <span className="italic" {...props} />
);

/**
 * Compact components for smaller message displays
 */
export const compactMarkdownComponents = {
  p: CompactP,
  ul: CompactUl,
  ol: CompactOl,
  li: CompactLi,
  strong: CompactStrong,
  em: CompactEm,
};
